import { z } from 'zod';
import { BIOME_IDS } from '../data/biomes.js';
import { CINEMATIC_IDS } from '../data/cinematics.js';
import { MOB_IDS, MOBS } from '../data/mobs.js';
import { AERIE_MODES, SHEFIRA_MOB_ID } from '../data/shefira.js';
import { TUTORIAL_ZONE_ID } from '../data/tutorial.js';
import { MAX_UPGRADE_LEVEL } from '../data/upgrades.js';
import { WORLD_EVENT_KINDS } from '../data/world-events.js';
import { MAX_LEVEL } from '../rules/progression.js';
import { MAX_MAP_TILES } from './move-to.js';
import { SHEFIRA_EVENT_PHASES } from './shefira.js';
/**
 * Sent to an admin alone at join (ADR 0022): the world editor may open.
 * `editorSaves` is false in production, where the edits file is part of the
 * build and the editor only looks.
 */
export const AdminEventSchema = z.object({ editorSaves: z.boolean() }).strict();
export const WorldEditsSavedSchema = z.object({ saved: z.literal(true) });
/** A purse that could overflow the database's integer is a bug waiting, not a gift. */
export const ADMIN_MAX_GOLD = 10_000_000;
/** Where the game master's window can send its admin by a button. */
export const ADMIN_TRAVEL_ZONES = [TUTORIAL_ZONE_ID, 'world'];
/** At most this many creatures a game master sets down at once, on the rings around them. */
export const GM_SPAWN_MAX = 20;
/**
 * What a creature a game master set down pays its killers (ADR 0025): all a
 * den's creature pays (experience, the kill counted, its loot), the
 * experience and the kill without loot, or nothing at all, for a show.
 */
export const GM_SPAWN_REWARDS = ['full', 'xp', 'none'];
/**
 * Every creature that fights, bosses included: a boss set down outside its
 * dungeon keeps its stats but none of its director's abilities. Not the
 * peaceful (nobody could fight them) nor Shefira, whose fight is her Aire's.
 */
export const GM_SPAWNABLE_MOB_IDS = MOB_IDS.filter((id) => !MOBS[id].peaceful && id !== SHEFIRA_MOB_ID);
/**
 * The game master's window (ADR 0025): an admin asks, on their own character
 * only, and the server checks the account's flag before anything moves. Items
 * and gold still go through the inventory and the ledger (CLAUDE.md rule 5),
 * journalled as `admin`.
 */
export const AdminCommandSchema = z.discriminatedUnion('action', [
    z
        .object({ action: z.literal('set_level'), level: z.number().int().min(1).max(MAX_LEVEL) })
        .strict(),
    z
        .object({
        action: z.literal('grant_gold'),
        amount: z.number().int().min(1).max(ADMIN_MAX_GOLD),
    })
        .strict(),
    z
        .object({
        action: z.literal('grant_item'),
        templateId: z.string().min(1).max(64),
        quantity: z.number().int().min(1).max(99),
        /** The smith's level the pieces arrive at; equipment only. */
        upgradeLevel: z.number().int().min(0).max(MAX_UPGRADE_LEVEL).default(0),
    })
        .strict(),
    z
        .object({
        action: z.literal('upgrade_worn'),
        level: z.number().int().min(0).max(MAX_UPGRADE_LEVEL),
    })
        .strict(),
    z.object({ action: z.literal('heal') }).strict(),
    /** Straight to a tile of the zone; a blocked one gives the nearest open tile. */
    z
        .object({
        action: z.literal('teleport'),
        x: z
            .number()
            .int()
            .min(0)
            .max(MAX_MAP_TILES - 1),
        y: z
            .number()
            .int()
            .min(0)
            .max(MAX_MAP_TILES - 1),
    })
        .strict(),
    /**
     * To another zone, at its arrival: the tutorial, which the world never leads
     * back to (ADR 0030), and the world again. Saved first, like an exit.
     */
    z.object({ action: z.literal('travel_zone'), zone: z.enum(ADMIN_TRAVEL_ZONES) }).strict(),
    /** A world event now (ADR 0050), after a short warning: in the world zone only. */
    z
        .object({
        action: z.literal('start_event'),
        kind: z.enum(WORLD_EVENT_KINDS),
        biome: z.enum(BIOME_IDS),
    })
        .strict(),
    /** End the event in progress, reward nobody. */
    z.object({ action: z.literal('stop_event') }).strict(),
    /** Creatures around the game master, for an event of their own: they hunt at once. */
    z
        .object({
        action: z.literal('spawn_mobs'),
        mob: z.enum(GM_SPAWNABLE_MOB_IDS),
        count: z.number().int().min(1).max(GM_SPAWN_MAX),
        reward: z.enum(GM_SPAWN_REWARDS),
    })
        .strict(),
    /** Every creature a game master set down in this zone goes, dead or alive. */
    z.object({ action: z.literal('clear_mobs') }).strict(),
    /**
     * Out of sight, or back: no other player is sent a vanished game master,
     * nor their pet, and creatures pay them no heed. Saved with the character.
     */
    z.object({ action: z.literal('set_vanished'), vanished: z.boolean() }).strict(),
    /** Beside a player of the same zone, by name (any case). */
    z
        .object({ action: z.literal('teleport_to_player'), name: z.string().trim().min(1).max(32) })
        .strict(),
    /** Every other player of the admin's zone, by name and tile, answered by `admin_players`. */
    z.object({ action: z.literal('list_players') }).strict(),
    /** Play one of Shefira's scenes (ADR 0067) on the admin's own screen, to look at it. */
    z.object({ action: z.literal('play_cinematic'), id: z.enum(CINEMATIC_IDS) }).strict(),
    /**
     * Into Shefira's Aire (ADR 0068) alone, from anywhere in the world: the
     * story with the Company, or the raid sized for its fewest players.
     * Nothing is asked of the quests or the party: it is for looking.
     */
    z.object({ action: z.literal('aerie_start'), mode: z.enum(AERIE_MODES) }).strict(),
    /** In the Aire: Shefira jumps to the start of a phase (her health set there). */
    z.object({ action: z.literal('aerie_phase'), phase: z.enum(SHEFIRA_EVENT_PHASES) }).strict(),
]);
/** What the command did, for the window's line; the new state arrives by `inventory` and `progress`. */
export const AdminResultSchema = z.object({
    action: z.enum([
        'set_level',
        'grant_gold',
        'grant_item',
        'upgrade_worn',
        'heal',
        'teleport',
        'travel_zone',
        'start_event',
        'stop_event',
        'play_cinematic',
        'aerie_start',
        'aerie_phase',
        'spawn_mobs',
        'clear_mobs',
        'set_vanished',
        'teleport_to_player',
        'list_players',
    ]),
    /**
     * Pieces changed by `upgrade_worn`, levels by `set_level`, creatures set
     * down by `spawn_mobs` or taken away by `clear_mobs`, 1 for `set_vanished`
     * out of sight, players listed by `list_players`, 0 otherwise.
     */
    count: z.number().int().min(0).default(0),
});
/** At most this many players in one `admin_players`: far more than a zone holds. */
export const ADMIN_PLAYERS_MAX = 1000;
/** The answer to `list_players`, to the admin alone: who else is in the zone, and where. */
export const AdminPlayersSchema = z
    .object({
    players: z
        .array(z
        .object({
        name: z.string().min(1).max(32),
        x: z.number().int().min(0),
        y: z.number().int().min(0),
    })
        .strict())
        .max(ADMIN_PLAYERS_MAX),
})
    .strict();
export const AdminRejectedSchema = z.object({
    reason: z.enum([
        'invalid',
        'not-admin',
        'unknown-item',
        'bag-full',
        'not-upgradeable',
        'dead',
        /** No open tile near where the teleport aimed: rock, deep water, the rim. */
        'blocked',
        /** An event is under way, or none is to stop, or this zone holds none (ADR 0050). */
        'event-busy',
        'no-event',
        /** Not in the Aire, or Shefira is not up (ADR 0068). */
        'no-aerie',
        /** Nobody of that name in this zone. */
        'not-found',
    ]),
});
//# sourceMappingURL=admin.js.map